import React, { createContext, useState, useMemo, useCallback, useEffect } from 'react'
import {
  Box,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  Paper,
  TableFooter,
  TablePagination,
  Skeleton,
  debounce,
} from '@mui/material'
import { useGetStaffBenefitClaims } from '../../api/staff-benefit-claim'
import ErrorMessage from '../shared/component/ErrorMessage'
import { ClaimReviewRow } from './ClaimReviewRow'
import { TablePaginationActions } from '../shared/component/TablePaginationActions'
import { DateFilter } from './DateFilter'
import { SearchBar } from './SearchBar'
import moment from 'moment'

const initState = {
  refetch: undefined,
}
export const ClaimReviewContext = createContext(initState)

const SkeletonRow = React.memo(({ height }) => (
  <TableRow>
    <TableCell colSpan='100%' height={height}>
      <Skeleton width='100%' height='100%' />
    </TableCell>
  </TableRow>
))

export const ClaimReviewTable = () => {
  const firstDayOfCurMonth = moment().startOf('month').format('YYYY-MM-DD')
  const today = moment().format('YYYY-MM-DD')
  const [startDate, setStartDate] = useState(firstDayOfCurMonth)
  const [endDate, setEndDate] = useState(today)
  const [key, setKey] = useState('')

  const { data, refetch, isLoading, error } = useGetStaffBenefitClaims({
    isAdminView: true,
    startDate,
    endDate,
    key,
  })

  const claims = data?.docs ?? []

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const arr = useMemo(() => {
    return rowsPerPage > 0
      ? [...claims].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : claims
  }, [page, rowsPerPage, claims])

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - claims?.length) : 0

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const debounceRefetch = useCallback(
    debounce(() => {
      refetch()
    }, 500),
    []
  )

  useEffect(() => {
    debounceRefetch()
  }, [rowsPerPage, page, startDate, endDate, key, debounceRefetch])

  return (
    <ClaimReviewContext.Provider value={{ refetch }}>
      <Box m='auto' display='flex' flexDirection='column'>
        <Box
          gap='12px'
          display='grid'
          gridTemplateColumns='repeat(auto-fit, minmax(200px,1fr))'
          mb={3}
        >
          <SearchBar setKey={setKey} />
          <DateFilter
            label='Start Date'
            name='startDate'
            value={startDate}
            setValue={setStartDate}
          />
          <DateFilter label='End Date' name='endDate' value={endDate} setValue={setEndDate} />
        </Box>
        {error ? (
          <ErrorMessage error={error} />
        ) : data?.docs?.length > 0 || isLoading ? (
          <TableContainer component={Paper}>
            <Table aria-label='table'>
              <TableHead>
                <TableRow>
                  <TableCell align='left'>Order Id</TableCell>
                  <TableCell align='left'>Applicant</TableCell>
                  <TableCell align='left'>Code</TableCell>
                  <TableCell align='left'>Name</TableCell>
                  <TableCell align='left'>Regional state</TableCell>
                  <TableCell align='left'>Quantity</TableCell>
                  <TableCell align='left'>Car Plate</TableCell>
                  <TableCell align='left'>Car Model</TableCell>
                  <TableCell align='left'>Supplier</TableCell>
                  <TableCell align='left'>Invoice</TableCell>
                  <TableCell align='left'>Received at</TableCell>
                  <TableCell align='left'>Processed by</TableCell>
                  <TableCell align='right'>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  Array(rowsPerPage)
                    .fill()
                    .map((_, idx) => <SkeletonRow key={idx} height={60} />)
                ) : (
                  <>
                    {arr.map((row) => {
                      return <ClaimReviewRow row={row} key={row.id} />
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={13} />
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 30, { label: 'All', value: -1 }]}
                    colSpan={13}
                    count={claims?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        'aria-label': 'rows per page',
                      },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          <ErrorMessage empty />
        )}
      </Box>
    </ClaimReviewContext.Provider>
  )
}
